import React from 'react'

type Participant = {
  firstName: string
  lastName: string
  role?: string[]
}

type InvitedEmail = {
  email?: string
  firstName?: string
  lastName?: string
}

interface InvitationSuccessModalProps {
  onClose: () => void
  meetingTitle: string
  date: string
  time: string
  timezone: string
  participants?: Participant[]
  invitedEmails?: InvitedEmail[]
}

const AppInvitationSuccessModal: React.FC<InvitationSuccessModalProps> = ({
  onClose,
  meetingTitle,
  date,
  time,
  timezone,
  participants = [],
  invitedEmails = [],
}) => {
  function getInitialsWithDots({
    firstName,
    lastName,
    email,
  }: {
    firstName?: string
    lastName?: string
    email?: string
  }): string {
    const firstInitial = firstName?.trim()?.[0]?.toUpperCase() || ''
    const lastInitial = lastName?.trim()?.[0]?.toUpperCase() || ''

    // Use name-based initials if available
    if (firstInitial || lastInitial) {
      return firstInitial + lastInitial
    }

    // Fallback to first letter of email username
    if (email) {
      const username = email.split('@')[0]?.trim()
      return username?.[0]?.toUpperCase() || ''
    }

    return ''
  }

  const colorClasses = [
    { bg: 'bg-orange-light', text: 'text-orange' },
    { bg: 'bg-green-lighter2x', text: 'text-green-dark' },
    { bg: 'bg-purple-lighter3x', text: 'text-purple-light' },
    { bg: 'bg-red-light', text: 'text-red' },
    { bg: 'bg-yellow-light', text: 'text-yellow-darkest' },
    { bg: 'bg-brown-light', text: 'text-brown-dark' },
    { bg: 'bg-sky-light', text: 'text-green' },
    { bg: 'bg-sky', text: 'text-blue-dark' },
  ]

  const getColorIndex = ({ email, firstName }) => {
    // Case 1: Use email if available
    if (email && typeof email === 'string' && email.length > 0) {
      let hash = 0
      for (let i = 0; i < email.length; i++) {
        hash = email.charCodeAt(i) + ((hash << 5) - hash)
      }
      return Math.abs(hash) % colorClasses.length
    }

    // Case 2: Use firstName if available
    if (firstName && typeof firstName === 'string' && firstName.length > 0) {
      let hash = 0
      for (let i = 0; i < firstName.length; i++) {
        hash = firstName.charCodeAt(i) + ((hash << 5) - hash)
      }
      return Math.abs(hash) % colorClasses.length
    }

    // Case 3: Generate random color
    return Math.floor(Math.random() * colorClasses.length)
  }
  return (
    <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg">
        {/* Header */}
        <div className="flex justify-between items-start mb-4 p-6 border-b border-[#ECECEC]">
          <div>
            <h2 className="text-lg font-semibold text-[#18181B] text-left m-0">
              Invitations Sent Successfully!
            </h2>
            <p className="text-sm text-[#5E5E5E] text-left mt-2">
              Meeting invitations have been sent to {participants.length} participants
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">
            <img
              src={require('../../../../appAssets/appImages/x-mark.png')}
              alt="close"
              width={20}
            />
          </button>
        </div>
        <div className="px-6">
          {/* Meeting Title */}
          <h3 className="text-md font-medium text-gray-900 mb-4 text-left">{meetingTitle}</h3>
        </div>

        <hr className="border-[#ECECEC]" />
        <div className="py-6">
          <div className="px-6">
            <p className="text-sm text-[#121212] text-left mb-2">Details</p>
            {/* Date & Time */}
            <div className="bg-gray-50 p-4 rounded-lg border border-[#ECECEC] mb-6">
              <div className="flex items-center mb-2 text-gray-700">
                <img
                  className="me-3"
                  src={require('../../../../appAssets/appImages/calendar.png')}
                  alt="close"
                  width={20}
                />
                {/* <svg
                  className="w-5 h-5 me-3 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3M16 7V3M3 11h18M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg> */}
                <span className="text-[#3F3F46]">{date}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <img
                  className="me-3"
                  src={require('../../../../appAssets/appImages/clock.png')}
                  alt="close"
                  width={20}
                />
                {/* <svg
                  className="w-5 h-5 me-3 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6l4 2"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg> */}
                <span className="text-[#3F3F46]">
                  {time} {timezone}
                </span>
              </div>
            </div>

            {/* Participants */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-[#121212] mb-3 text-left flex items-center">
                <img
                  src={require('../../../../appAssets/appImages/participants.png')}
                  alt="close"
                  width={20}
                  className="me-3"
                />
                Participants ({participants.length})
              </h4>
              <ul className="space-y-2 p-0">
                {participants.map((p) => (
                  <li className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`${
                          colorClasses[
                            getColorIndex({
                              email: '',
                              firstName: p?.firstName,
                            })
                          ]?.bg
                        } ${
                          colorClasses[
                            getColorIndex({
                              email: p?.firstName,
                              firstName: '',
                            })
                          ]?.text
                        } w-8 h-8 rounded-full bg-[#E6F6D1] text-[#629C28] flex items-center justify-center text-[10px] font-semibold capitalize`}
                      >
                        {getInitialsWithDots({
                          firstName: p?.firstName,
                          lastName: p?.lastName,
                          email: '',
                        })}
                      </div>
                      <span className="text-sm">
                        {p.firstName} {p.lastName}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {p?.role?.[0] === 'BSO_ADVISOR' && 'Advisor'}
                      {p?.role?.[0] === 'BUSINESS_OWNER' && 'Team Member'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Emails */}
          <div className="p-6 border-t border-b border-[#ececec] mb-6">
            <h4 className="text-sm font-medium text-[#121212] mb-2 text-left">
              Invitations sent to:
            </h4>
            <div className="flex flex-wrap gap-2">
              {invitedEmails.map((u) => (
                <span
                  key={u.email}
                  className="bg-[#EAE9FE] text-[#5C37EB] px-2 py-2 rounded-lg text-sm"
                >
                  {`${u.firstName} ${u.lastName}` || u.email}
                </span>
              ))}
            </div>
          </div>
          <div className="px-6">
            {/* Done Button */}
            <button
              onClick={onClose}
              className="bg-purple hover:bg-violet-700 text-white text-sm font-medium px-4 py-2 rounded-md"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppInvitationSuccessModal
